import React, { useEffect, useState } from "react";
import { api, apiAssetUrl } from "@/lib/api";
import { toast } from "sonner";
import PlatformTransactionQrisPanel from "@/components/PlatformTransactionQrisPanel";
import {
  Building2, Eye, EyeOff, Info, QrCode, Save, TestTube2,
  Trash2, Upload, Zap,
} from "lucide-react";

const EMPTY_PAYMENT = {
  bank_name: "",
  bank_account_no: "",
  bank_holder: "",
  instructions: "",
  qris_image_url: "",
  updated_at: "",
};

const EMPTY_YOKKE = {
  enabled: false,
  is_production: false,
  yokke_client_key: "",
  yokke_client_secret: "",
  yokke_private_key_pem: "",
  yokke_partner_id: "",
  yokke_merchant_id: "",
  yokke_terminal_id: "",
  yokke_channel_id: "02",
  has_client_secret: false,
  has_private_key: false,
  configured: false,
  ready: false,
};

export default function PlatformQris() {
  const [paymentInfo, setPaymentInfo] = useState(EMPTY_PAYMENT);
  const [yokke, setYokke] = useState(EMPTY_YOKKE);
  const [qrisFile, setQrisFile] = useState(null);
  const [qrisPreview, setQrisPreview] = useState("");
  const [savingPayment, setSavingPayment] = useState(false);
  const [savingYokke, setSavingYokke] = useState(false);
  const [testingYokke, setTestingYokke] = useState(false);
  const [uploadingQris, setUploadingQris] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const load = async () => {
    const [paymentResponse, yokkeResponse] = await Promise.all([
      api.get("/platform-payment-info"),
      api.get("/admin/platform-payment-info/yokke"),
    ]);
    setPaymentInfo({ ...EMPTY_PAYMENT, ...(paymentResponse.data || {}) });
    setYokke({ ...EMPTY_YOKKE, ...(yokkeResponse.data || {}), yokke_client_secret: "", yokke_private_key_pem: "" });
  };

  useEffect(() => {
    load().catch((error) => {
      toast.error(error?.response?.data?.detail || "Gagal memuat metode pembayaran langganan");
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!qrisFile) {
      setQrisPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(qrisFile);
    setQrisPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [qrisFile]);

  const savePaymentInfo = async () => {
    setSavingPayment(true);
    try {
      const response = await api.put("/admin/platform-payment-info", {
        bank_name: paymentInfo.bank_name || "",
        bank_account_no: paymentInfo.bank_account_no || "",
        bank_holder: paymentInfo.bank_holder || "",
        instructions: paymentInfo.instructions || "",
      });
      setPaymentInfo({ ...EMPTY_PAYMENT, ...(response.data || {}) });
      toast.success("Informasi pembayaran manual tersimpan");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal menyimpan informasi pembayaran");
    } finally {
      setSavingPayment(false);
    }
  };

  const saveYokke = async () => {
    setSavingYokke(true);
    try {
      const response = await api.put("/admin/platform-payment-info/yokke", {
        enabled: !!yokke.enabled,
        is_production: !!yokke.is_production,
        yokke_client_key: yokke.yokke_client_key || "",
        yokke_client_secret: yokke.yokke_client_secret || "",
        yokke_private_key_pem: yokke.yokke_private_key_pem || "",
        yokke_partner_id: yokke.yokke_partner_id || "",
        yokke_merchant_id: yokke.yokke_merchant_id || "",
        yokke_terminal_id: yokke.yokke_terminal_id || "",
        yokke_channel_id: yokke.yokke_channel_id || "02",
      });
      setYokke({ ...EMPTY_YOKKE, ...(response.data || {}), yokke_client_secret: "", yokke_private_key_pem: "" });
      toast.success("Konfigurasi QRIS Dinamis Yokke tersimpan");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal menyimpan konfigurasi Yokke");
    } finally {
      setSavingYokke(false);
    }
  };

  const testYokke = async () => {
    setTestingYokke(true);
    try {
      await api.post("/admin/platform-payment-info/yokke/test");
      toast.success("Koneksi Yokke berhasil");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Koneksi Yokke gagal");
    } finally {
      setTestingYokke(false);
    }
  };

  const uploadPaymentQris = async () => {
    if (!qrisFile) return toast.error("Pilih file QRIS terlebih dahulu");
    setUploadingQris(true);
    try {
      const body = new FormData();
      body.append("qris_file", qrisFile);
      const response = await api.post("/admin/platform-payment-info/qris", body);
      setPaymentInfo({ ...EMPTY_PAYMENT, ...(response.data || {}) });
      setQrisFile(null);
      toast.success("QRIS statis pembayaran langganan berhasil diunggah");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal mengunggah QRIS");
    } finally {
      setUploadingQris(false);
    }
  };

  const deletePaymentQris = async () => {
    if (!window.confirm("Hapus QRIS statis pembayaran langganan?")) return;
    try {
      await api.delete("/admin/platform-payment-info/qris");
      setPaymentInfo((previous) => ({ ...previous, qris_image_url: "", qris_file_id: null }));
      toast.success("QRIS statis pembayaran langganan dihapus");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal menghapus QRIS");
    }
  };

  const savedQrisUrl = paymentInfo.qris_image_url
    ? `${apiAssetUrl(paymentInfo.qris_image_url)}?v=${encodeURIComponent(paymentInfo.updated_at || "1")}`
    : "";

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-8">
        <div className="overline text-zinc-500">PLATFORM</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Pembayaran Langganan</h1>
        <p className="mt-2 text-sm text-zinc-500 max-w-3xl">
          Atur QRIS Dinamis Yokke, QRIS statis, dan transfer bank untuk pembayaran paket mitra.
        </p>
      </div>

      <div className="mb-6 border border-blue-200 bg-blue-50 p-4 flex gap-3 text-sm text-blue-900">
        <Info className="h-5 w-5 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Khusus pembayaran langganan mitra</div>
          <div className="mt-1">Konfigurasi ini tidak digunakan untuk transaksi QRIS outlet di kasir.</div>
        </div>
      </div>

      <section className="mb-10">
        <div className="mb-3">
          <div className="overline text-[#002FA7]">AUTO APPROVAL</div>
          <h2 className="font-display font-black text-2xl tracking-tight">QRIS Dinamis Yokke</h2>
          <p className="text-sm text-zinc-500 mt-1">Pembayaran sukses di Yokke otomatis mengaktifkan paket tanpa persetujuan manual owner.</p>
        </div>
        <div className="border border-border bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 bg-blue-50 text-[#002FA7] flex items-center justify-center"><Zap className="h-5 w-5" /></div>
              <div>
                <div className="font-semibold">Status konfigurasi</div>
                <div className={`text-xs mt-1 ${yokke.ready ? "text-emerald-700" : "text-amber-700"}`}>
                  {yokke.ready ? "Aktif dan siap digunakan" : yokke.configured ? "Lengkap, tetapi belum diaktifkan" : "Belum lengkap"}
                </div>
              </div>
            </div>
            <label className="flex items-center gap-3 border border-border px-4 py-3 cursor-pointer">
              <span className="text-sm font-semibold">Aktifkan QRIS Dinamis</span>
              <input type="checkbox" checked={!!yokke.enabled} onChange={(event) => setYokke({ ...yokke, enabled: event.target.checked })} />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="overline">Environment</label>
              <select value={yokke.is_production ? "production" : "development"} onChange={(event) => setYokke({ ...yokke, is_production: event.target.value === "production" })} className="w-full border border-border px-3 py-2.5 mt-1 bg-white">
                <option value="development">Development / UAT</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div>
              <label className="overline">Channel ID</label>
              <input value={yokke.yokke_channel_id || "02"} onChange={(event) => setYokke({ ...yokke, yokke_channel_id: event.target.value })} maxLength={2} className="w-full border border-border px-3 py-2.5 mt-1 font-mono" />
            </div>
            <div>
              <label className="overline">Client Key</label>
              <input value={yokke.yokke_client_key || ""} onChange={(event) => setYokke({ ...yokke, yokke_client_key: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono" />
            </div>
            <div>
              <label className="overline">Partner ID</label>
              <input value={yokke.yokke_partner_id || ""} onChange={(event) => setYokke({ ...yokke, yokke_partner_id: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono" />
            </div>
            <div>
              <label className="overline">Merchant ID</label>
              <input value={yokke.yokke_merchant_id || ""} onChange={(event) => setYokke({ ...yokke, yokke_merchant_id: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono" />
            </div>
            <div>
              <label className="overline">Terminal ID</label>
              <input value={yokke.yokke_terminal_id || ""} onChange={(event) => setYokke({ ...yokke, yokke_terminal_id: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="overline">Client Secret</label>
              <div className="relative mt-1">
                <input type={showSecret ? "text" : "password"} value={yokke.yokke_client_secret || ""} onChange={(event) => setYokke({ ...yokke, yokke_client_secret: event.target.value })} className="w-full border border-border px-3 py-2.5 pr-11 font-mono" placeholder={yokke.has_client_secret ? "Kosongkan untuk mempertahankan secret tersimpan" : "Masukkan client secret"} />
                <button type="button" onClick={() => setShowSecret((value) => !value)} className="absolute right-3 top-2.5 text-zinc-500">{showSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="overline">Private Key Body Base64</label>
              <textarea rows={7} value={yokke.yokke_private_key_pem || ""} onChange={(event) => setYokke({ ...yokke, yokke_private_key_pem: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono text-xs" placeholder={yokke.has_private_key ? "Kosongkan untuk mempertahankan private key tersimpan" : "Tanpa header BEGIN/END PRIVATE KEY"} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-5">
            <button onClick={saveYokke} disabled={savingYokke} className="py-3 bg-[#002FA7] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"><Save className="h-4 w-4" />{savingYokke ? "Menyimpan…" : "Simpan Konfigurasi Yokke"}</button>
            <button onClick={testYokke} disabled={testingYokke || !yokke.ready} className="py-3 border border-zinc-900 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"><TestTube2 className="h-4 w-4" />{testingYokke ? "Menguji…" : "Test Koneksi"}</button>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-3">
          <div className="overline text-[#002FA7]">VERIFIKASI MANUAL</div>
          <h2 className="font-display font-black text-2xl tracking-tight">QRIS Statis & Transfer Bank</h2>
          <p className="text-sm text-zinc-500 mt-1">Mitra mengunggah bukti pembayaran dan owner melakukan approve atau reject.</p>
        </div>
        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-2 border border-border bg-zinc-50 p-6">
            <div className="flex items-center gap-2 mb-4"><QrCode className="h-5 w-5" /><div className="overline">QRIS STATIS</div></div>
            <div className="bg-white border border-border min-h-72 flex items-center justify-center p-4">
              {qrisPreview || savedQrisUrl ? <img src={qrisPreview || savedQrisUrl} alt="QRIS pembayaran langganan" className="max-h-72 w-full object-contain" /> : <div className="text-center text-zinc-400"><QrCode className="h-16 w-16 mx-auto mb-2" /><div className="text-sm">QRIS belum diunggah</div></div>}
            </div>
            <label className="mt-4 border-2 border-dashed border-zinc-300 p-4 flex flex-col items-center cursor-pointer hover:border-[#002FA7] hover:bg-blue-50/50">
              <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={(event) => setQrisFile(event.target.files?.[0] || null)} />
              <Upload className="h-6 w-6 text-zinc-500 mb-1" /><span className="text-sm font-semibold">{qrisFile?.name || "Pilih gambar QRIS"}</span><span className="text-xs text-zinc-500 mt-1">JPG, PNG, atau WEBP · maksimal 5 MB</span>
            </label>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={uploadPaymentQris} disabled={!qrisFile || uploadingQris} className="py-2.5 bg-[#002FA7] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"><Upload className="h-4 w-4" />{uploadingQris ? "Mengunggah…" : "Upload"}</button>
              <button onClick={deletePaymentQris} disabled={!savedQrisUrl} className="py-2.5 border border-rose-600 text-rose-600 text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2 hover:bg-rose-600 hover:text-white"><Trash2 className="h-4 w-4" />Hapus</button>
            </div>
          </div>

          <div className="lg:col-span-3 border border-border bg-white p-6">
            <div className="flex items-center gap-2 mb-5"><Building2 className="h-5 w-5" /><div className="overline">TRANSFER BANK</div></div>
            <div className="space-y-4">
              <div><label className="overline">Nama Bank</label><input value={paymentInfo.bank_name || ""} onChange={(event) => setPaymentInfo({ ...paymentInfo, bank_name: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="overline">Nomor Rekening</label><input value={paymentInfo.bank_account_no || ""} onChange={(event) => setPaymentInfo({ ...paymentInfo, bank_account_no: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono" /></div>
                <div><label className="overline">Atas Nama</label><input value={paymentInfo.bank_holder || ""} onChange={(event) => setPaymentInfo({ ...paymentInfo, bank_holder: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1" /></div>
              </div>
              <div><label className="overline">Instruksi Pembayaran</label><textarea rows={5} value={paymentInfo.instructions || ""} onChange={(event) => setPaymentInfo({ ...paymentInfo, instructions: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1" /></div>
              <button onClick={savePaymentInfo} disabled={savingPayment} className="w-full bg-zinc-900 text-white py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"><Save className="h-4 w-4" />{savingPayment ? "Menyimpan…" : "Simpan Informasi Pembayaran"}</button>
            </div>
          </div>
        </div>
      </section>
      <PlatformTransactionQrisPanel />
    </div>
  );
}
